import * as _ from 'lodash'; //{ isEmpty, isString, isNumber, uniqBy }
import { AbstractControl } from '@angular/forms';
import * as moment from 'moment';
import { isMedicineDailyOccurrenceOption, MedicineOccurrence } from '../types/Medicine';

export const SYSTOLIC_UPPER_LIMIT = 210;
export const SYSTOLIC_LOWER_LIMIT = 80;
export const DIASTOLIC_UPPER_LIMIT = 130;
export const DIASTOLIC_LOWER_LIMIT = 40;
export const HEART_RATE_UPPER_LIMIT = 160;
export const HEART_RATE_LOWER_LIMIT = 40;

export const ALLOWED_INPUT_DATE_FORMATS: Array<any> = ['YYYY-MM-DD', moment.ISO_8601];

export enum ValidationErrors {
  REQUIRED = 'required',
  INVALID_EMAIL_ADDRESS = 'invalidEmailAddress',
  INVALID_PASSWORD = 'invalidPassword',
  INVALID_AUS_PHONE = 'invalidAusPhone',
  INVALID_DATE = 'invalidDate',
  DATE_NOT_IN_PAST = 'dateNotInPast',
  DATE_NOT_IN_FUTURE = 'dateNotInFuture',
  INVALID_DAILY_OCCURRENCE = 'medicineDailyOccurrence',
  INVALID_DOSAGE = 'invalidDosage',
  WHOLE_NUMBER = 'wholeNumber',
  GREATER_THAN = 'greaterThan',
  LESS_THAN = 'lessThan',
  INVALID_SYSTOLIC = 'invalidSystolic',
  INVALID_DIASTOLIC = 'invalidDiastolic',
  INVALID_HEART_RATE = 'invalidHeartRate',
  NOT_A_NUMBER = 'notANumber',
  INVALID_VALUE = 'pattern',
}

export type ValidationMessages = { [I in ValidationErrors]: string };
export type ValidationResult = { [I in ValidationErrors]?: any } | null;
export type Validator = (control: AbstractControl) => ValidationResult;

export class ValidationUtil {

  static VALID_EMAIL: RegExp =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

  static VALID_PASSWORD: RegExp =
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;

  static VALID_AUS_MOBILE_NUMBER: RegExp =
    /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

  static WHOLE_NUMBER: RegExp = /^-?\d*(?:\.0+)?$/;
  static DECIMAL_NUMBER: RegExp = /^\d+(\.\d{1,3})?$/;
  static getValidatorErrorMessage(
    validatorName: keyof ValidationMessages,
    validatorValue?: any,
    fieldName?: any
  ) {
    if (String(fieldName).length > 30) {
      fieldName = 'This field';
    }
    const config: ValidationMessages = {
      required: `${fieldName} is required`,
      invalidEmailAddress: `${fieldName} is not formatted correctly`,
      invalidPassword:
        'Password must be at least 8 characters long, and contain a number', // TODO: Probably won't use password field
      invalidAusPhone: `${fieldName} Must be 10 digits starting with '04'`,
      invalidDate: 'Must be a valid date', // Should probably never happen in normal circumstances
      dateNotInPast: `${fieldName} must be a date in the past`,
      dateNotInFuture: `${fieldName} must be a date in the future`,
      medicineDailyOccurrence: `${fieldName} must be a number between 1 and 12, or 24`,
      invalidDosage: `${fieldName} should be valid`,
      wholeNumber: `${fieldName} must be a whole number (no decimals)`,
      notANumber: `${fieldName} must be number`,
      invalidSystolic: `${fieldName} must be between ${SYSTOLIC_LOWER_LIMIT} and ${SYSTOLIC_UPPER_LIMIT}`,
      invalidDiastolic: `${fieldName} must be between ${DIASTOLIC_LOWER_LIMIT} and ${DIASTOLIC_UPPER_LIMIT}`,
      invalidHeartRate: `${fieldName} must be between ${HEART_RATE_LOWER_LIMIT} and ${HEART_RATE_UPPER_LIMIT}`,
      greaterThan: `${fieldName} must be more than ${validatorValue}`,
      lessThan: `${fieldName} must be less than ${validatorValue}`,
      pattern: `Invalid value for ${fieldName}`,
    };

    return config[validatorName];
  }

  static isRequired(control: AbstractControl): ValidationResult {
    const val = control.value;

    if (!_.isNumber(val) && val !== true && _.isEmpty(val)) {
      return { [ValidationErrors.REQUIRED]: true };
    } else {
      return null;
    }
  }

  static emailValid(control: AbstractControl): ValidationResult {
    // RFC 2822 compliant regex
    if (control.value.match(ValidationUtil.VALID_EMAIL)) {
      return null;
    } else {
      return { [ValidationErrors.INVALID_EMAIL_ADDRESS]: true };
    }
  }

  static passwordValid(control: AbstractControl): ValidationResult {
    if (control.value.match(ValidationUtil.VALID_PASSWORD)) {
      return null;
    } else {
      return { [ValidationErrors.INVALID_PASSWORD]: true };
    }
  }

  static validAustralianMobileNumber(
    control: AbstractControl
  ): ValidationResult {
    if (control.value.match(ValidationUtil.VALID_AUS_MOBILE_NUMBER)) {
      return null;
    } else {
      return { [ValidationErrors.INVALID_AUS_PHONE]: true };
    }
  }

  static dateInPast(control: AbstractControl): ValidationResult {
    const parsedDate: moment.Moment = moment(control.value, ALLOWED_INPUT_DATE_FORMATS, true);

    if (!parsedDate.isValid()) {
      return { [ValidationErrors.INVALID_DATE]: true };
    }

    const today: moment.Moment = moment().endOf('day');

    if (parsedDate.startOf('day').isBefore(today)) {
      return null;
    } else {
      return { [ValidationErrors.DATE_NOT_IN_PAST]: true };
    }
  }

  static dateInFuture(control: AbstractControl): ValidationResult {
    const parsedDate: moment.Moment = moment(control.value, ALLOWED_INPUT_DATE_FORMATS, true);

    if (!parsedDate.isValid()) {
      return { [ValidationErrors.INVALID_DATE]: true };
    }

    const today: moment.Moment = moment().startOf('day');

    if (parsedDate.startOf('day').isSameOrAfter(today)) {
      return null;
    } else {
      return { [ValidationErrors.DATE_NOT_IN_FUTURE]: true };
    }
  }

  static isValidMedicineDailyOccurrenceOption(
    control: AbstractControl
  ): ValidationResult {
    if (!isMedicineDailyOccurrenceOption(+control.value)) {
      return { [ValidationErrors.INVALID_DAILY_OCCURRENCE]: true };
    }

    return null;
  }

  static isDosageValid(control: AbstractControl) {
    const occurrences: MedicineOccurrence[] = control.value || [];
    
    const invalidDosage = occurrences.some((occurrence) => {
        if (occurrence.dose) return occurrence.dose < 0.25;

        return false;
    });
    
    if (invalidDosage || _.uniqBy(occurrences, 'time').length !== occurrences.length) {
      return { [ValidationErrors.INVALID_DOSAGE]: true };
    }

    return {};
  }

  static isWholeNumber(control: AbstractControl): ValidationResult {
    const val = control.value;
    if (_.isString(val)) {
      if (control.value.match(ValidationUtil.WHOLE_NUMBER)) {
        return null;
      } else {
        return { [ValidationErrors.WHOLE_NUMBER]: true };
      }
    } else if (_.isNumber(val)) {
      if (Number.isInteger(val)) {
        return null;
      } else {
        return { [ValidationErrors.WHOLE_NUMBER]: true };
      }
    } else {
      return { [ValidationErrors.WHOLE_NUMBER]: true };
    }
  }

  static isGreaterThan(limit: number): Validator {
    return (control: AbstractControl) => {
      if (control.value > limit) {
        return null;
      }

      return { [ValidationErrors.GREATER_THAN]: limit };
    };
  }

  static isLessThan(limit: number): Validator {
    return (control: AbstractControl) => {
      if (control.value < limit) {
        return null;
      }

      return { [ValidationErrors.LESS_THAN]: limit };
    };
  }

  static isValidSystolic(control: AbstractControl): ValidationResult {
    const isBelowUpper = +control.value < SYSTOLIC_UPPER_LIMIT + 1;
    const isAboveLower = +control.value > SYSTOLIC_LOWER_LIMIT - 1;
    const isEmptyString = control.value === '';
    const validSystolic = isBelowUpper && isAboveLower || isEmptyString;

    if (validSystolic) {
      return null;
    }
    return { [ValidationErrors.INVALID_SYSTOLIC]: true };
  }

  static isValidDiastolic(control: AbstractControl): ValidationResult {
    const isBelowUpper = +control.value < DIASTOLIC_UPPER_LIMIT + 1;
    const isAboveLower = +control.value > DIASTOLIC_LOWER_LIMIT - 1;
    const isEmptyString = control.value === '';
    const validDiastolic = isBelowUpper && isAboveLower || isEmptyString;

    if (validDiastolic) {
      return null;
    }
    return { [ValidationErrors.INVALID_DIASTOLIC]: true };
  }

  static isValidHeartRate(control: AbstractControl): ValidationResult {
    const isBelowUpper = +control.value < HEART_RATE_UPPER_LIMIT + 1;
    const isAboveLower = +control.value > HEART_RATE_LOWER_LIMIT - 1;
    const isEmptyString = control.value === '';
    const validHeartRate = (isBelowUpper && isAboveLower) || isEmptyString;

    if (validHeartRate) {
      return null;
    }
    return { [ValidationErrors.INVALID_HEART_RATE]: true };
  }
  /*
  ** https://stackoverflow.com/questions/308122/simple-regular-expression-for-a-decimal-with-a-precision-of-2
  ** Modified our method to allow for precision of 3.
  */
  static isNotANumber(control: AbstractControl): ValidationResult {
    if (control.value.match(ValidationUtil.DECIMAL_NUMBER)) {
      return null;
    }
    return { [ValidationErrors.NOT_A_NUMBER]: true };
  }
}
